import { useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent, ReactNode } from "react";
import { HeadquartersArt, RegionArt } from "~/components/ui/civic-art";
import { REGION_ART_LABEL } from "~/components/ui/civic-figure";
import { type MessageKey, useI18n } from "~/i18n";
import { playPlaceChime, unlockPlaceChime } from "~/lib/audio/place-chime";
import {
  BRAZIL_MAP_HEIGHT,
  BRAZIL_MAP_VIEWBOX,
  BRAZIL_MAP_WIDTH,
  BRAZIL_UF_DRAW_ORDER,
  BRAZIL_UF_SHAPES,
} from "~/lib/domain/brazil-geometry";
import {
  BACKCHANNEL_SAO_PAULO_OFFICE,
  type BrazilRegion,
  type BrazilUf,
  backchannelOfficeAddress,
  backchannelOfficeMapsUrl,
  brazilUfCapital,
  brazilUfName,
  brazilUfRegion,
  brazilUfTone,
} from "~/lib/domain/brazil-ufs";
import { BRAZIL_UF_COUNT, WATCHED_SEFAZ_COUNT, isWatchedSefaz } from "~/lib/domain/watched-sefaz";

type MapSelection = { kind: "uf"; uf: BrazilUf } | { kind: "hq" };
type MapAnchor = { left: number; top: number };

const UF_ORDER = BRAZIL_UF_DRAW_ORDER;
/** The HQ point as a share of the map box: the SVG fills the stage width and
 * sets its height, so percentages land on the same map coordinate at any size. */
const HQ_POSITION: CSSProperties = {
  left: `${(BACKCHANNEL_SAO_PAULO_OFFICE.mapPoint.x / BRAZIL_MAP_WIDTH) * 100}%`,
  top: `${(BACKCHANNEL_SAO_PAULO_OFFICE.mapPoint.y / BRAZIL_MAP_HEIGHT) * 100}%`,
};

const REGION_COPY: Record<BrazilRegion, MessageKey> = {
  north: "overview.coverage.region.north",
  northeast: "overview.coverage.region.northeast",
  centralWest: "overview.coverage.region.centralWest",
  southeast: "overview.coverage.region.southeast",
  south: "overview.coverage.region.south",
};

const FACT_KEY: Record<BrazilUf, MessageKey> = {
  AC: "auth.map.fact.AC",
  AL: "auth.map.fact.AL",
  AP: "auth.map.fact.AP",
  AM: "auth.map.fact.AM",
  BA: "auth.map.fact.BA",
  CE: "auth.map.fact.CE",
  DF: "auth.map.fact.DF",
  ES: "auth.map.fact.ES",
  GO: "auth.map.fact.GO",
  MA: "auth.map.fact.MA",
  MT: "auth.map.fact.MT",
  MS: "auth.map.fact.MS",
  MG: "auth.map.fact.MG",
  PA: "auth.map.fact.PA",
  PB: "auth.map.fact.PB",
  PR: "auth.map.fact.PR",
  PE: "auth.map.fact.PE",
  PI: "auth.map.fact.PI",
  RJ: "auth.map.fact.RJ",
  RN: "auth.map.fact.RN",
  RS: "auth.map.fact.RS",
  RO: "auth.map.fact.RO",
  RR: "auth.map.fact.RR",
  SC: "auth.map.fact.SC",
  SP: "auth.map.fact.SP",
  SE: "auth.map.fact.SE",
  TO: "auth.map.fact.TO",
};

function clampAnchor(left: number, top: number, width: number): MapAnchor {
  return {
    left: Math.min(width - 88, Math.max(88, left)),
    top: Math.max(72, top),
  };
}

export function BrazilAuthMap() {
  const { t } = useI18n();
  const stageRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<MapSelection | null>(null);
  const [hover, setHover] = useState<(MapSelection & MapAnchor) | null>(null);
  const [activeUf, setActiveUf] = useState<BrazilUf>("SP");
  useEffect(() => {
    const onPointerDown = () => unlockPlaceChime();
    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, []);

  function stageSize() {
    return stageRef.current?.getBoundingClientRect().width ?? BRAZIL_MAP_WIDTH;
  }

  function anchorFromElement(target: Element): MapAnchor {
    const stage = stageRef.current;
    if (!stage) return { left: 140, top: 120 };
    const targetRect = target.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    return clampAnchor(
      targetRect.left + targetRect.width / 2 - stageRect.left,
      targetRect.top - stageRect.top,
      stageRect.width,
    );
  }

  function dismissAll() {
    setHover(null);
    setSelection(null);
  }

  function anchorForSelection(next: MapSelection): MapAnchor {
    const stage = stageRef.current;
    if (!stage) return { left: stageSize() / 2, top: 120 };
    if (next.kind === "hq") {
      const pin = stage.querySelector(".tax-auth-map__hq-dot");
      if (pin) return anchorFromElement(pin);
    } else {
      const path = stage.querySelector(`[data-uf="${next.uf}"]`);
      if (path) return anchorFromElement(path);
    }
    return {
      left: (BACKCHANNEL_SAO_PAULO_OFFICE.mapPoint.x / BRAZIL_MAP_WIDTH) * stage.clientWidth,
      top: (BACKCHANNEL_SAO_PAULO_OFFICE.mapPoint.y / BRAZIL_MAP_HEIGHT) * stage.clientHeight,
    };
  }

  function chooseUf(uf: BrazilUf) {
    setHover(null);
    setActiveUf(uf);
    setSelection((current) =>
      current?.kind === "uf" && current.uf === uf ? null : { kind: "uf", uf },
    );
  }

  function chooseHq() {
    setHover(null);
    setActiveUf("SP");
    setSelection((current) => (current?.kind === "hq" ? null : { kind: "hq" }));
  }

  function handleHitPointerEnter(event: PointerEvent<HTMLAnchorElement>, uf: BrazilUf) {
    if (event.pointerType === "touch") return;
    setActiveUf(uf);
    setHover({ kind: "uf", uf, ...anchorFromElement(event.currentTarget) });
    playPlaceChime(uf, "uf");
  }

  function handleHitKeyDown(event: KeyboardEvent<HTMLAnchorElement>) {
    if (event.key === "Escape") {
      dismissAll();
      event.preventDefault();
      return;
    }

    let nextIndex: number | undefined;
    const currentIndex = UF_ORDER.indexOf(activeUf);
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (currentIndex + 1) % UF_ORDER.length;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (currentIndex - 1 + UF_ORDER.length) % UF_ORDER.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = UF_ORDER.length - 1;
    } else if (event.key === " ") {
      event.preventDefault();
      chooseUf(activeUf);
      return;
    }

    if (nextIndex === undefined) return;
    event.preventDefault();
    const next = UF_ORDER[nextIndex];
    if (!next) return;
    setActiveUf(next);
    setHover(null);
    event.currentTarget
      .closest("svg")
      ?.querySelector<HTMLAnchorElement>(`a[data-uf="${next}"]`)
      ?.focus();
  }

  const selectedUf =
    selection?.kind === "uf" ? selection.uf : selection?.kind === "hq" ? "SP" : null;
  const cardAnchor = selection ? anchorForSelection(selection) : null;
  const tooltip = hover && !selection ? hover : null;

  return (
    <div className="tax-auth-map">
      <div
        ref={stageRef}
        className="tax-auth-map__stage"
        onKeyDown={(event) => {
          if (event.key !== "Escape" || event.defaultPrevented) return;
          dismissAll();
        }}
      >
        <svg
          className="tax-auth-map__svg"
          viewBox={BRAZIL_MAP_VIEWBOX}
          aria-labelledby="tax-auth-map-title"
        >
          <title id="tax-auth-map-title">{t("auth.map.title")}</title>
          {/* Outline pass: every UF stroked wide in ink, then the fills below
           * cover the inner half of each edge, so only the country's outer
           * contour keeps a visible ink line. It draws itself once. */}
          <g className="tax-auth-map__outline">
            {BRAZIL_UF_SHAPES.map(({ uf, path }) => (
              <path key={uf} d={path} pathLength={1} />
            ))}
          </g>
          <g className="tax-auth-map__states">
            {BRAZIL_UF_SHAPES.map(({ uf, path }) => {
              const watched = isWatchedSefaz(uf);
              const tone = brazilUfTone(uf);
              const label = t("auth.map.stateLabel", {
                state: brazilUfName(uf),
                uf,
                capital: brazilUfCapital(uf) ?? "—",
                watch: t(watched ? "auth.map.watched" : "auth.map.unwatched"),
              });
              return (
                // biome-ignore lint/a11y/useValidAnchor: SVG cannot contain a button; the fragment is keyboard-native navigation to the place card.
                <a
                  key={uf}
                  className="tax-auth-map__hit"
                  href="#tax-auth-map-place"
                  tabIndex={activeUf === uf ? 0 : -1}
                  data-uf={uf}
                  aria-label={label}
                  aria-current={selectedUf === uf ? "true" : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    chooseUf(uf);
                  }}
                  onFocus={() => setActiveUf(uf)}
                  onPointerEnter={(event) => handleHitPointerEnter(event, uf)}
                  onPointerLeave={() => setHover(null)}
                  onKeyDown={handleHitKeyDown}
                >
                  <path
                    d={path}
                    className="tax-auth-map__state"
                    data-watch={watched ? "watched" : "dim"}
                    data-tone={tone}
                    data-selected={selectedUf === uf ? "true" : undefined}
                    data-active={activeUf === uf ? "true" : undefined}
                  >
                    <title>{label}</title>
                  </path>
                </a>
              );
            })}
          </g>
        </svg>

        {/* São Paulo office: an 8px lime dot with a white halo on the office
         * point, a haloed leader into the sea and a typographic label that is
         * always on screen — its meaning never depends on hover. */}
        <div className="tax-auth-map__hq" style={HQ_POSITION}>
          <svg className="tax-auth-map__hq-leader" viewBox="0 0 40 32" aria-hidden="true">
            <path className="tax-auth-map__hq-leader-halo" d="M3 3 L18 26 H40" />
            <path d="M3 3 L18 26 H40" />
          </svg>
          <span className="tax-auth-map__hq-label" aria-hidden="true">
            <strong>{t("auth.map.hq.label")}</strong>
            <span>{t("auth.atlas.place")}</span>
          </span>
          <button
            type="button"
            className="tax-auth-map__hq-dot"
            aria-label={t("auth.map.hq.pinLabel")}
            aria-pressed={selection?.kind === "hq"}
            onClick={(event) => {
              event.stopPropagation();
              chooseHq();
            }}
            onFocus={() => setHover(null)}
            onKeyDown={(event) => {
              if (event.key !== "Escape") return;
              event.preventDefault();
              dismissAll();
            }}
            onPointerEnter={(event) => {
              if (event.pointerType === "touch") return;
              setHover(null);
              playPlaceChime("hq", "hq");
            }}
          >
            <span aria-hidden="true" />
          </button>
        </div>

        {tooltip ? <MapTooltip preview={tooltip} /> : null}

        {selection && cardAnchor ? (
          <PlaceCard selection={selection} anchor={cardAnchor} onClose={() => setSelection(null)} />
        ) : null}
      </div>

      {/* The context line under the map says what it holds; the green tones
       * only separate neighbours, so they get no legend ramp. A key appears
       * only if some UF ever leaves the watched list. */}
      <span id="tax-auth-map-place" hidden />
      {WATCHED_SEFAZ_COUNT < BRAZIL_UF_COUNT ? (
        <p className="tax-auth-map__legend" aria-label={t("auth.map.legend.label")}>
          <span className="tax-auth-map__swatch" data-watch="dim" aria-hidden="true" />
          {t("auth.map.legend.unwatched")}
        </p>
      ) : null}
    </div>
  );
}

function MapTooltip({ preview }: { preview: MapSelection & MapAnchor }) {
  const { t } = useI18n();
  const isHq = preview.kind === "hq";
  const uf = isHq ? BACKCHANNEL_SAO_PAULO_OFFICE.uf : preview.uf;
  const watched = isWatchedSefaz(uf);
  const tone = brazilUfTone(uf);

  return (
    <div
      className="tax-auth-map__tooltip"
      style={{ left: preview.left, top: preview.top }}
      aria-hidden="true"
    >
      <span
        className="tax-auth-map__swatch"
        data-watch={watched ? "watched" : "dim"}
        data-tone={tone}
      />
      <span>
        <strong>{isHq ? t("auth.map.hq.name") : brazilUfName(uf)}</strong>
        <small>{isHq ? t("auth.map.hq.role") : uf}</small>
        <p>{isHq ? t("auth.map.hq.fact") : t(FACT_KEY[uf])}</p>
      </span>
    </div>
  );
}

function PlaceCard({
  selection,
  anchor,
  onClose,
}: {
  selection: MapSelection;
  anchor: MapAnchor;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const isHq = selection.kind === "hq";
  const uf = isHq ? BACKCHANNEL_SAO_PAULO_OFFICE.uf : selection.uf;
  const region = brazilUfRegion(uf);
  const watched = isWatchedSefaz(uf);
  const imageAlt = isHq
    ? t("auth.map.hq.illustrationAlt")
    : t(region ? REGION_ART_LABEL[region] : "auth.map.illustrationKind");

  return (
    <article
      className={[
        "tax-auth-map__card",
        anchor.left < 200 ? "tax-auth-map__card--end" : "",
        isHq ? "tax-auth-map__card--shop" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ left: anchor.left, top: anchor.top }}
      aria-labelledby="tax-auth-place-title"
    >
      <div className="tax-auth-map__card-image">
        {isHq ? (
          <HeadquartersArt label={imageAlt} />
        ) : (
          <RegionArt region={region ?? "southeast"} highlightUf={uf} label={imageAlt} />
        )}
      </div>
      <div className="tax-auth-map__card-body">
        <div className="tax-auth-map__card-heading">
          <span className="tax-auth-map__card-eyebrow">{isHq ? t("auth.map.hq.eyebrow") : uf}</span>
          <h2 id="tax-auth-place-title">{isHq ? t("auth.map.hq.name") : brazilUfName(uf)}</h2>
          <p>{isHq ? t("auth.map.hq.role") : region ? t(REGION_COPY[region]) : null}</p>
        </div>
        <button type="button" className="tax-auth-map__card-close" onClick={onClose}>
          {t("auth.map.close")}
        </button>
        {isHq ? (
          <dl className="tax-auth-map__facts">
            <div>
              <dt>{t("auth.map.hq.address")}</dt>
              <dd>{backchannelOfficeAddress()}</dd>
            </div>
            <div>
              <dt>{t("auth.map.hq.phone")}</dt>
              <dd>
                <a href={`tel:${BACKCHANNEL_SAO_PAULO_OFFICE.phone.replace(/\s/g, "")}`}>
                  {BACKCHANNEL_SAO_PAULO_OFFICE.phone}
                </a>
              </dd>
            </div>
          </dl>
        ) : (
          <dl className="tax-auth-map__facts">
            <div>
              <dt>{t("auth.map.capital")}</dt>
              <dd>{brazilUfCapital(uf)}</dd>
            </div>
            <div>
              <dt>{t("auth.map.sefaz")}</dt>
              <dd>{t(watched ? "auth.map.watched" : "auth.map.unwatched")}</dd>
            </div>
          </dl>
        )}
        {isHq ? (
          <a
            className="btn btn--primary tax-auth-map__maps"
            href={backchannelOfficeMapsUrl()}
            target="_blank"
            rel="noreferrer"
          >
            {t("auth.map.hq.openMaps")}
          </a>
        ) : null}
      </div>
    </article>
  );
}

/**
 * Compact atlas for phones and tablets: a small, non-interactive green
 * silhouette of the same geometry (no 27 tiny touch targets) with the office
 * dot, next to the context line and the office label; children (the value
 * points) follow under a hairline. Shown below 1024px, where the
 * full interactive atlas does not fit beside the card.
 */
export function BrazilAtlasBand({ context, children }: { context: string; children?: ReactNode }) {
  const { t } = useI18n();
  const { x, y } = BACKCHANNEL_SAO_PAULO_OFFICE.mapPoint;
  return (
    <aside className="tax-auth-band" aria-label={t("auth.atlas.compactLabel")}>
      <svg
        className="tax-auth-band__map"
        viewBox={BRAZIL_MAP_VIEWBOX}
        aria-hidden="true"
        focusable="false"
      >
        <g className="tax-auth-band__outline">
          {BRAZIL_UF_SHAPES.map(({ uf, path }) => (
            <path key={uf} d={path} />
          ))}
        </g>
        <g className="tax-auth-band__states">
          {BRAZIL_UF_SHAPES.map(({ uf, path }) => (
            <path key={uf} d={path} data-tone={brazilUfTone(uf)} />
          ))}
        </g>
        <circle className="tax-auth-band__hq" cx={x} cy={y} r={26} />
      </svg>
      <div className="tax-auth-band__text">
        <p className="tax-auth-band__context">{context}</p>
        <p className="tax-auth-band__place">
          <span className="tax-auth-band__dot" aria-hidden="true" />
          {t("auth.map.hq.label")} · {t("auth.atlas.place")}
        </p>
      </div>
      {children}
    </aside>
  );
}
